/* eslint-disable jsx-a11y/img-redundant-alt */
import moment from "moment";
import React from "react";
import placeholderImg from "../../portals/admin/assets/image-placeholder.svg";
import { letters } from "../../portals/customer/pages/orders/dummyData";
import styles from "./OrderExpandCell.module.css";

const euro = (value) => `€${(Number(value) || 0).toFixed(2)}`;
const date = (value) => (value ? moment(value).format("YYYY-MM-DD") : "Not available");

const isEmpty = (value) => value === "Not available" || value === null || value === undefined || value === "";

const Field = ({ label, children }) => (
  <div>
    <dt>{label}</dt>
    <dd>{isEmpty(children) ? <span className={styles.none} title="Not available">—</span> : children}</dd>
  </div>
);

function LineItem({ item, orderNumber, refunded }) {
  const quantity = item?.fulfillable_quantity || 1;
  const price = Number(item?.original_price) || 0;
  return (
    <div className={`${styles.item} ${refunded ? styles.refunded : ""}`}>
      <img
        className={styles.thumb}
        src={item?.variant_image || placeholderImg}
        onError={(e) => {
          e.target.src = placeholderImg;
        }}
        alt="product image"
      />
      <div className={styles.itemBody}>
        <div className={styles.itemTitle} title={item?.title}>
          {item?.title || orderNumber}
        </div>
        {item?.title && (
          <div className={styles.meta}>
            Order <strong>{orderNumber}</strong>
          </div>
        )}
        <div className={styles.meta}>
          SKU <strong>{item?.sku || "Not available"}</strong>
        </div>
        {item?.variant_title && (
          <div className={styles.meta}>
            Variant <strong>{item.variant_title}</strong>
          </div>
        )}
      </div>
      <div className={styles.price}>
        {euro(price * quantity)}
        <span>
          {euro(price)} × {quantity}
        </span>
      </div>
    </div>
  );
}

function Tracking({ info }) {
  if (Array.isArray(info)) {
    return info.length
      ? info.map((t, i) => (
          <a key={t?.tracking_number || i} href={`https://t.17track.net/en#nums=${t?.tracking_number}`} target="_blank" rel="noreferrer">
            {t?.tracking_number || "Not available"}
            <br />
          </a>
        ))
      : "Not available";
  }
  return info?.tracking_number ? (
    <a href={`https://www.yuntrack.com/parcelTracking?id=${info.tracking_number}`} target="_blank" rel="noreferrer">
      {info.tracking_number}
    </a>
  ) : (
    "Not available"
  );
}

const OrderExpandCell = ({ row, finalShippingCost }) => {
  const fulfillmentTransaction = row?.transaction_numbers?.length > 0 && row.transaction_numbers[0];
  const refundTransactions = row?.transaction_numbers?.filter((t) => !t?.fulfillment_transaction) || [];
  const showTransactions =
    !["Pending", "Failed", "Waiting"].includes(row?.status) && fulfillmentTransaction;
  const shipping = row?.shipping_details || {};
  const address = [shipping.address1, shipping.address2].filter(Boolean).join(", ");
  const customer = row?.customer_info
    ? [row.customer_info.first_name, row.customer_info.last_name].filter(Boolean).join(" ")
    : "";

  return (
    <div className={styles.panel}>
      <section className={styles.section}>
        <h3 className={styles.heading}>Items</h3>
        {row?.line_items?.map((item, i) => (
          <LineItem key={item?._id || i} item={item} orderNumber={`${row?.shopify_order_number}-${letters[i]}`} />
        ))}
        {row?.refunded_line_items?.length > 0 && (
          <>
            <h4 className={styles.subheading}>Cancelled & refunded items</h4>
            {row.refunded_line_items.map((item, i) => (
              <LineItem key={item?._id || i} item={item} orderNumber={`${row?.shopify_order_number}-${letters[i]}`} refunded />
            ))}
          </>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.heading}>Status</h3>
        <dl className={styles.list}>
          <Field label="Order status">{row?.status || "Not available"}</Field>
          <Field label="Shopify financial status">
            <span className={styles.status}>{row?.financial_status || "Not available"}</span>
          </Field>
          {(row?.status === "Processing" || row?.status === "Shipped") && (
            <Field label="Date fulfilled">{date(row?.updatedAt)}</Field>
          )}
          {row?.status === "Shipped" && (
            <Field label="Tracking">
              <Tracking info={row?.tracking_info} />
            </Field>
          )}
          {showTransactions && (
            <div className={styles.group}>
              <Field label="Transaction (F)">
                {fulfillmentTransaction?.fulfillment_transaction || "Not available"}
              </Field>
              <Field label="Amount">
                {fulfillmentTransaction?.amount != null ? euro(fulfillmentTransaction.amount) : "Not available"}
              </Field>
            </div>
          )}
          {showTransactions &&
            refundTransactions.map((t, i) => (
              <div key={t?.refund_transaction || i} className={styles.group}>
                <Field label="Transaction (R)">{t?.refund_transaction || "Not available"}</Field>
                <Field label="Amount">{t?.amount != null ? euro(t.amount) : "Not available"}</Field>
                {t?.refund_amount_type && <Field label="Refund amount type">{t.refund_amount_type}</Field>}
                {t?.refund_reason && <Field label="Refund note">{t.refund_reason}</Field>}
              </div>
            ))}
        </dl>
      </section>

      <section className={styles.section}>
        <h3 className={styles.heading}>Pricing</h3>
        <dl className={styles.list}>
          <Field label="Product cost">{euro(row?.pricing?.product_cost)}</Field>
          <Field label={`Shipping cost ${row?.shipping_method === "premium" ? "(P)" : "(R)"}`}>
            {isNaN(row?.pricing?.total_shipping_cost) ? "€0" : `€${finalShippingCost(row)}`}
          </Field>
          <Field label="Created at Shopify">{date(row?.created_at_shopify)}</Field>
        </dl>
      </section>

      <section className={styles.section}>
        <h3 className={styles.heading}>Customer</h3>
        <dl className={styles.list}>
          <Field label="Name">{customer || "Not available"}</Field>
          <Field label="Country">{shipping.shipping_country || "Not available"}</Field>
          <Field label="Address">{address || "Not available"}</Field>
          <Field label="Postal code">{shipping.zip || "Not available"}</Field>
        </dl>
      </section>
    </div>
  );
};

export default OrderExpandCell;
